import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2, X, Plus, Trash2, CheckCircle, XCircle, FlaskConical } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type {
  ModuleConfigurationAdmin,
  UpdateModuleConfigRequest,
  MigrationProperty,
  MigrationPropertyType,
} from '@/types';
import { ModuleExecutionMode } from '@/types';
import { parseMetaFields, serializeMetaFields } from '@/lib/utils/metaFields';

const BRIDGE_PRODUCT_TYPES: Array<{ value: number; label: string }> = [
  { value: -1, label: 'Admin' },
  { value: 1, label: 'SimpleChurchCRM' },
  { value: 2, label: 'Arena' },
  { value: 3, label: 'Financials' },
  { value: 4, label: 'Ezekiel' },
  { value: 5, label: 'Formbuilder' },
  { value: 6, label: 'Giving' },
  { value: 7, label: 'Innovate' },
  { value: 8, label: 'Elexio' },
  { value: 9, label: 'FellowshipOne' },
  { value: 10, label: 'ParishSoft' },
  { value: 11, label: 'ProtectMyMinistry' },
  { value: 12, label: 'CloverSites' },
  { value: 13, label: 'SiteOrganic' },
  { value: 14, label: 'MinistryOne' },
  { value: 15, label: 'ShareFaithWeb' },
  { value: 16, label: 'ChurchStreamingTV' },
  { value: 17, label: 'ShareFaithKids' },
  { value: 18, label: 'E360' },
  { value: 19, label: 'ShelbyFinancials' },
  { value: 20, label: 'WorshipPlanning' },
  { value: 21, label: 'Educate' },
  { value: 22, label: 'TADS' },
  { value: 23, label: 'FaithDirect' },
  { value: 24, label: 'Ravenna' },
  { value: 25, label: 'ShareFaithMedia' },
  { value: 26, label: 'LandingPage' },
  { value: 27, label: 'ProjectUnity' },
  { value: 28, label: 'HGSCommunication' },
  { value: 29, label: 'StudioAI' },
  { value: 30, label: 'CMG' },
  { value: 99, label: 'Formbuilder_Client' },
];

const AMPLIFY_MODULES: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Giving Admin' },
  { value: 2, label: 'People' },
  { value: 3, label: 'Mobile App' },
  { value: 5, label: 'Accounting' },
  { value: 6, label: 'Websites' },
  { value: 7, label: 'Media' },
  { value: 8, label: 'Kids' },
  { value: 9, label: 'Streaming' },
  { value: 10, label: 'Service Planning' },
  { value: 12, label: 'Bible & Sermons' },
  { value: 14, label: 'My Parish' },
  { value: 15, label: 'Families' },
  { value: 16, label: 'Tuition' },
  { value: 17, label: 'Ministry Scheduler' },
  { value: 18, label: 'Faith Formation' },
  { value: 19, label: 'Development' },
  { value: 20, label: 'Offering' },
  { value: 21, label: 'Safety' },
  { value: 22, label: 'Intelligent Query' },
  { value: 23, label: 'Communications' },
  { value: 24, label: 'Equip' },
  { value: 25, label: 'CloverSites' },
  { value: 26, label: 'Amplify Intelligence' },
];

const moduleConfigSchema = z
  .object({
    displayName: z.string().min(1, 'Display name is required').max(200),
    migrateEndpoint: z.string().optional(),
    validateEndpoint: z.string().optional(),
    userMigrationGetEndpoint: z.string().optional(),
    userMigrationPutEndpoint: z.string().optional(),
    sourceIdValidationRegex: z.string().optional(),
    sourceIdPlaceholder: z.string().optional(),
    timeoutSeconds: z.number().min(30).max(3600),
    maxRetries: z.number().min(0).max(10),
    retryDelaySeconds: z.number().min(0).max(300),
    baseUrl: z.string().url('Must be a valid URL').max(500).optional().or(z.literal('')),
    apiKeySecretName: z.string().max(100).optional().or(z.literal('')),
    appId: z.string().max(200).optional().or(z.literal('')),
    executionMode: z.enum([ModuleExecutionMode.Synchronous, ModuleExecutionMode.AsyncPolled]),
    jobStatusEndpoint: z.string().max(500).optional().or(z.literal('')),
    pollIntervalSeconds: z.number().min(5).max(3600),
    pollMaxAttempts: z.number().min(1).max(1000),
    metadataUpdatesJson: z.string().optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    // Metadata bindings are independent of execution mode — validate whenever present.
    if (data.metadataUpdatesJson && data.metadataUpdatesJson.trim() !== '') {
      try {
        const parsed = JSON.parse(data.metadataUpdatesJson);
        if (!Array.isArray(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['metadataUpdatesJson'],
            message: 'Must be a JSON array of { metaKey, valueFrom } bindings.',
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['metadataUpdatesJson'],
          message: 'Not valid JSON.',
        });
      }
    }

    if (data.executionMode !== ModuleExecutionMode.AsyncPolled) return;

    if (!data.jobStatusEndpoint || data.jobStatusEndpoint.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['jobStatusEndpoint'],
        message: 'Required when execution mode is Async Polled.',
      });
    }
  });

type FormValues = z.infer<typeof moduleConfigSchema>;

function getDefaultValue(type: MigrationPropertyType): string | number | boolean {
  switch (type) {
    case 'string':
      return '';
    case 'integer':
      return 0;
    case 'boolean':
      return false;
  }
}

interface ModuleConfigEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: ModuleConfigurationAdmin | null;
  onSubmit: (key: string, data: UpdateModuleConfigRequest) => void;
  isSubmitting?: boolean | undefined;
}

export function ModuleConfigEditor({
  open,
  onOpenChange,
  module,
  onSubmit,
  isSubmitting,
}: ModuleConfigEditorProps) {
  const [migrationProperties, setMigrationProperties] = useState<MigrationProperty[]>([]);
  const [propertiesDirty, setPropertiesDirty] = useState(false);
  const [bridgeProductTypeId, setBridgeProductTypeId] = useState<number | null>(
    module?.bridgeProductTypeId ?? null,
  );
  const [amplifyModuleId, setAmplifyModuleId] = useState<number | null>(
    module?.amplifyModuleId ?? null,
  );
  const [testIdValue, setTestIdValue] = useState('');
  const [testIdResult, setTestIdResult] = useState<boolean | null>(null);

  const defaultFormValues: FormValues = {
    displayName: module?.displayName ?? '',
    migrateEndpoint: module?.migrateEndpoint ?? '',
    validateEndpoint: module?.validateEndpoint ?? '',
    userMigrationGetEndpoint: module?.userMigrationGetEndpoint ?? '',
    userMigrationPutEndpoint: module?.userMigrationPutEndpoint ?? '',
    sourceIdValidationRegex: module?.sourceIdValidationRegex ?? '',
    sourceIdPlaceholder: module?.sourceIdPlaceholder ?? '',
    timeoutSeconds: module?.timeoutSeconds ?? 300,
    maxRetries: module?.maxRetries ?? 3,
    retryDelaySeconds: module?.retryDelaySeconds ?? 30,
    baseUrl: module?.baseUrl ?? '',
    apiKeySecretName: module?.apiKeySecretName ?? '',
    appId: module?.appId ?? '',
    executionMode: module?.executionMode ?? ModuleExecutionMode.Synchronous,
    jobStatusEndpoint: module?.jobStatusEndpoint ?? '',
    pollIntervalSeconds: module?.pollIntervalSeconds ?? 30,
    pollMaxAttempts: module?.pollMaxAttempts ?? 60,
    metadataUpdatesJson: module?.metadataUpdatesJson ?? '',
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(moduleConfigSchema),
    defaultValues: defaultFormValues,
  });

  const executionMode = watch('executionMode');
  const isAsync = executionMode === ModuleExecutionMode.AsyncPolled;

  // Reset form and migration properties when module changes
  useEffect(() => {
    if (module) {
      reset({
        displayName: module.displayName ?? '',
        migrateEndpoint: module.migrateEndpoint ?? '',
        validateEndpoint: module.validateEndpoint ?? '',
        userMigrationGetEndpoint: module.userMigrationGetEndpoint ?? '',
        userMigrationPutEndpoint: module.userMigrationPutEndpoint ?? '',
        sourceIdValidationRegex: module.sourceIdValidationRegex ?? '',
        sourceIdPlaceholder: module.sourceIdPlaceholder ?? '',
        timeoutSeconds: module.timeoutSeconds,
        maxRetries: module.maxRetries,
        retryDelaySeconds: module.retryDelaySeconds,
        baseUrl: module.baseUrl ?? '',
        apiKeySecretName: module.apiKeySecretName ?? '',
        appId: module.appId ?? '',
        executionMode: module.executionMode,
        jobStatusEndpoint: module.jobStatusEndpoint ?? '',
        pollIntervalSeconds: module.pollIntervalSeconds,
        pollMaxAttempts: module.pollMaxAttempts,
        metadataUpdatesJson: module.metadataUpdatesJson ?? '',
      });
      setMigrationProperties(parseMetaFields(module.metaFields));
      setPropertiesDirty(false);
      setBridgeProductTypeId(module.bridgeProductTypeId ?? null);
      setAmplifyModuleId(module.amplifyModuleId ?? null);
      setTestIdValue('');
      setTestIdResult(null);
    }
  }, [module, reset]);

  const sourceIdValidationRegex = watch('sourceIdValidationRegex');
  const sourceIdPlaceholder = watch('sourceIdPlaceholder');

  const handleFormSubmit = (data: FormValues) => {
    if (!module) return;

    const baseUrlChanged = (data.baseUrl ?? '') !== (module.baseUrl ?? '');
    const apiKeySecretNameChanged = (data.apiKeySecretName ?? '') !== (module.apiKeySecretName ?? '');
    const appIdChanged = (data.appId ?? '') !== (module.appId ?? '');
    const validationChanged =
      (data.sourceIdValidationRegex ?? '') !== (module.sourceIdValidationRegex ?? '') ||
      (data.sourceIdPlaceholder ?? '') !== (module.sourceIdPlaceholder ?? '');
    const bridgeProductTypeIdChanged = bridgeProductTypeId !== (module.bridgeProductTypeId ?? null);
    const amplifyModuleIdChanged = amplifyModuleId !== (module.amplifyModuleId ?? null);
    const userMigrationGetChanged =
      (data.userMigrationGetEndpoint ?? '') !== (module.userMigrationGetEndpoint ?? '');
    const userMigrationPutChanged =
      (data.userMigrationPutEndpoint ?? '') !== (module.userMigrationPutEndpoint ?? '');

    const asyncExecutionChanged =
      data.executionMode !== module.executionMode ||
      (data.jobStatusEndpoint ?? '') !== (module.jobStatusEndpoint ?? '') ||
      data.pollIntervalSeconds !== module.pollIntervalSeconds ||
      data.pollMaxAttempts !== module.pollMaxAttempts;

    // Metadata bindings are independent of execution mode and carry their own update flag.
    const metadataUpdatesChanged =
      (data.metadataUpdatesJson ?? '') !== (module.metadataUpdatesJson ?? '');

    onSubmit(module.moduleKey, {
      displayName: data.displayName,
      migrateEndpoint: data.migrateEndpoint,
      validateEndpoint: data.validateEndpoint,
      timeoutSeconds: data.timeoutSeconds,
      maxRetries: data.maxRetries,
      retryDelaySeconds: data.retryDelaySeconds,
      metaFields: serializeMetaFields(migrationProperties),
      ...(baseUrlChanged && {
        baseUrl: data.baseUrl || null,
        updateBaseUrl: true,
      }),
      ...(apiKeySecretNameChanged && {
        apiKeySecretName: data.apiKeySecretName || null,
        updateApiKeySecretName: true,
      }),
      ...(appIdChanged && {
        appId: data.appId || null,
        updateAppId: true,
      }),
      ...(validationChanged && {
        sourceIdValidationRegex: data.sourceIdValidationRegex || null,
        updateSourceIdValidation: true,
        sourceIdPlaceholder: data.sourceIdPlaceholder || null,
      }),
      ...(bridgeProductTypeIdChanged && {
        bridgeProductTypeId: bridgeProductTypeId,
        updateBridgeProductTypeId: true,
      }),
      ...(userMigrationGetChanged && {
        userMigrationGetEndpoint: data.userMigrationGetEndpoint || null,
        updateUserMigrationGetEndpoint: true,
      }),
      ...(userMigrationPutChanged && {
        userMigrationPutEndpoint: data.userMigrationPutEndpoint || null,
        updateUserMigrationPutEndpoint: true,
      }),
      ...(amplifyModuleIdChanged && {
        amplifyModuleId: amplifyModuleId,
        updateAmplifyModuleId: true,
      }),
      ...(asyncExecutionChanged && {
        executionMode: data.executionMode,
        jobStatusEndpoint: data.jobStatusEndpoint || null,
        pollIntervalSeconds: data.pollIntervalSeconds,
        pollMaxAttempts: data.pollMaxAttempts,
        updateAsyncExecution: true,
      }),
      ...(metadataUpdatesChanged && {
        metadataUpdatesJson: data.metadataUpdatesJson || null,
        updateMetadataUpdates: true,
      }),
    });
  };

  const testIdFormat = (testValue: string): boolean => {
    try {
      const regex = new RegExp(sourceIdValidationRegex ?? '');
      return regex.test(testValue);
    } catch {
      return false;
    }
  };

  const addProperty = () => {
    setMigrationProperties((prev) => [
      ...prev,
      { key: '', label: '', type: 'string', value: '' },
    ]);
    setPropertiesDirty(true);
  };

  const removeProperty = (index: number) => {
    setMigrationProperties((prev) => prev.filter((_, i) => i !== index));
    setPropertiesDirty(true);
  };

  const updateProperty = (
    index: number,
    field: keyof MigrationProperty,
    value: string | number | boolean,
  ) => {
    setMigrationProperties((prev) =>
      prev.map((prop, i) => {
        if (i !== index) return prop;

        if (field === 'type') {
          // When type changes, reset value to default for new type
          const newType = value as MigrationPropertyType;
          return { ...prop, type: newType, value: getDefaultValue(newType) };
        }

        return { ...prop, [field]: value };
      }),
    );
    setPropertiesDirty(true);
  };

  const isFormDirty =
    isDirty ||
    propertiesDirty ||
    bridgeProductTypeId !== (module?.bridgeProductTypeId ?? null) ||
    amplifyModuleId !== (module?.amplifyModuleId ?? null);

  if (!module) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[95vw] overflow-y-auto sm:max-w-[920px]">
        <DialogHeader>
          <DialogTitle>Edit Module Configuration</DialogTitle>
          <DialogDescription>
            Configure settings for {module.displayName} ({module.moduleKey})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Vertical tab layout: sidebar of section labels on the left, panel on the
              right. Replaces a 6-column horizontal grid that overflowed its 700px dialog
              once Async/Polling and Custom Properties were added. */}
          <Tabs defaultValue="general" orientation="vertical" className="mt-4 flex flex-col gap-4 md:flex-row md:gap-6">
            <TabsList className="flex h-auto w-full shrink-0 flex-row gap-1 overflow-x-auto rounded-md bg-muted p-1 md:w-44 md:flex-col md:overflow-visible">
              <TabsTrigger value="general" className="w-full justify-center md:justify-start">General</TabsTrigger>
              <TabsTrigger value="connection" className="w-full justify-center md:justify-start">Connection</TabsTrigger>
              <TabsTrigger value="endpoints" className="w-full justify-center md:justify-start">Endpoints</TabsTrigger>
              <TabsTrigger value="async" className="w-full justify-center md:justify-start">Async/Polling</TabsTrigger>
              <TabsTrigger value="metadata" className="w-full justify-center md:justify-start">Metadata Updates</TabsTrigger>
              <TabsTrigger value="validation" className="w-full justify-center md:justify-start">Validation</TabsTrigger>
              <TabsTrigger value="migration" className="w-full justify-center md:justify-start">Custom Properties</TabsTrigger>
            </TabsList>

            <div className="min-w-0 flex-1">
              {/* Each TabsContent below renders into this panel. mt-0 overrides the
                  default mt-2 spacing since the sidebar provides horizontal separation. */}
            <TabsContent value="general" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input id="displayName" {...register('displayName')} />
                  {errors.displayName && (
                    <p className="text-sm text-destructive">{errors.displayName.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="moduleKey">Module Key</Label>
                  <Input
                    id="moduleKey"
                    value={module.moduleKey}
                    readOnly
                    disabled
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    The module key is the system identity and cannot be changed.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Execution Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setValue('executionMode', ModuleExecutionMode.Synchronous, { shouldDirty: true })}
                    className={cn(
                      'rounded-md border p-3 text-left text-sm transition-colors',
                      executionMode === ModuleExecutionMode.Synchronous
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <div className="font-medium">Synchronous</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Single POST returns success/failure inline (default).
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue('executionMode', ModuleExecutionMode.AsyncPolled, { shouldDirty: true })}
                    className={cn(
                      'rounded-md border p-3 text-left text-sm transition-colors',
                      executionMode === ModuleExecutionMode.AsyncPolled
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <div className="font-medium">Async Polled</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Module returns jobId; orchestrator polls until complete.
                    </div>
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Async-polled modules expose additional configuration on the Async/Polling tab.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="timeoutSeconds">Timeout (seconds)</Label>
                  <Input
                    id="timeoutSeconds"
                    type="number"
                    {...register('timeoutSeconds', { valueAsNumber: true })}
                  />
                  {errors.timeoutSeconds && (
                    <p className="text-sm text-destructive">{errors.timeoutSeconds.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">30 - 3600 seconds</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxRetries">Max Retries</Label>
                  <Input
                    id="maxRetries"
                    type="number"
                    {...register('maxRetries', { valueAsNumber: true })}
                  />
                  {errors.maxRetries && (
                    <p className="text-sm text-destructive">{errors.maxRetries.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="retryDelaySeconds">Retry Delay (seconds)</Label>
                  <Input
                    id="retryDelaySeconds"
                    type="number"
                    {...register('retryDelaySeconds', { valueAsNumber: true })}
                  />
                  {errors.retryDelaySeconds && (
                    <p className="text-sm text-destructive">{errors.retryDelaySeconds.message}</p>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="connection" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label htmlFor="baseUrl">Base URL</Label>
                <Input
                  id="baseUrl"
                  placeholder="https://api.example.com/"
                  className="font-mono text-sm"
                  {...register('baseUrl')}
                />
                {errors.baseUrl && (
                  <p className="text-sm text-destructive">{errors.baseUrl.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Override the default base URL for this module's API calls. Leave empty for system default.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="apiKeySecretName">API Key Secret Name</Label>
                <Input
                  id="apiKeySecretName"
                  placeholder="MyModuleApiKey"
                  className="font-mono text-sm"
                  {...register('apiKeySecretName')}
                />
                {errors.apiKeySecretName && (
                  <p className="text-sm text-destructive">{errors.apiKeySecretName.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Name of the configuration key or Key Vault secret holding the API key.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="appId">App ID</Label>
                <Input
                  id="appId"
                  placeholder="my-app-id"
                  className="font-mono text-sm"
                  {...register('appId')}
                />
                {errors.appId && (
                  <p className="text-sm text-destructive">{errors.appId.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Optional application identifier sent as X-App-Id header. Required by some platforms (e.g., Giving).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bridgeProductTypeId">Bridge Product Type</Label>
                <Select
                  value={bridgeProductTypeId !== null ? String(bridgeProductTypeId) : 'none'}
                  onValueChange={(value) =>
                    setBridgeProductTypeId(value === 'none' ? null : parseInt(value, 10))
                  }
                >
                  <SelectTrigger id="bridgeProductTypeId">
                    <SelectValue placeholder="Select product type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {BRIDGE_PRODUCT_TYPES.map((pt) => (
                      <SelectItem key={pt.value} value={String(pt.value)}>
                        {pt.label} ({pt.value})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Maps this module to a Bridge product type for automatic detection during org lookup.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amplifyModuleId">Amplify Module</Label>
                <Select
                  value={amplifyModuleId !== null ? String(amplifyModuleId) : 'none'}
                  onValueChange={(value) =>
                    setAmplifyModuleId(value === 'none' ? null : parseInt(value, 10))
                  }
                >
                  <SelectTrigger id="amplifyModuleId">
                    <SelectValue placeholder="Select Amplify module..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {AMPLIFY_MODULES.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>
                        {m.label} ({m.value})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Amplify module to provision after migration. Must match the Amplify platform's module ID.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="endpoints" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label htmlFor="validateEndpoint">Validate Endpoint</Label>
                <Input
                  id="validateEndpoint"
                  placeholder="/api/validate"
                  {...register('validateEndpoint')}
                />
                {errors.validateEndpoint && (
                  <p className="text-sm text-destructive">{errors.validateEndpoint.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="migrateEndpoint">Migration Endpoint</Label>
                <Input
                  id="migrateEndpoint"
                  placeholder="/api/migrate"
                  {...register('migrateEndpoint')}
                />
                {errors.migrateEndpoint && (
                  <p className="text-sm text-destructive">{errors.migrateEndpoint.message}</p>
                )}
              </div>

              <div className="border-t pt-4 space-y-4">
                <div>
                  <h4 className="text-sm font-medium">User Migration (Optional)</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Set the GET endpoint to enable Phase 2 user migration for this module. The
                    PUT (UUID write-back) endpoint is optional — leave it blank for modules that
                    already record the Amplify UUID during provisioning, and the write-back step
                    is skipped. Use <code className="font-mono">{'{sourceId}'}</code> as a placeholder.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="userMigrationGetEndpoint">GET Users Endpoint</Label>
                  <Input
                    id="userMigrationGetEndpoint"
                    placeholder="/api/migration/users/{sourceId}"
                    className="font-mono text-sm"
                    {...register('userMigrationGetEndpoint')}
                  />
                  {errors.userMigrationGetEndpoint && (
                    <p className="text-sm text-destructive">
                      {errors.userMigrationGetEndpoint.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="userMigrationPutEndpoint">
                    PUT UUIDs Endpoint <span className="text-muted-foreground font-normal">(optional — blank skips write-back)</span>
                  </Label>
                  <Input
                    id="userMigrationPutEndpoint"
                    placeholder="/api/migration/users/{sourceId}"
                    className="font-mono text-sm"
                    {...register('userMigrationPutEndpoint')}
                  />
                  {errors.userMigrationPutEndpoint && (
                    <p className="text-sm text-destructive">
                      {errors.userMigrationPutEndpoint.message}
                    </p>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="async" className="space-y-4 mt-0">
              {!isAsync ? (
                <div className="rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
                  Module is in <span className="font-medium">Synchronous</span> mode. Switch to{' '}
                  <span className="font-medium">Async Polled</span> on the General tab to configure
                  job polling.
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="jobStatusEndpoint">Job Status Endpoint</Label>
                    <Input
                      id="jobStatusEndpoint"
                      placeholder="api/jobs/{jobId}"
                      className="font-mono text-sm"
                      {...register('jobStatusEndpoint')}
                    />
                    {errors.jobStatusEndpoint && (
                      <p className="text-sm text-destructive">{errors.jobStatusEndpoint.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      GET endpoint the orchestrator polls until the module job completes. Use{' '}
                      <code className="font-mono">{'{jobId}'}</code> as a placeholder.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pollIntervalSeconds">Poll Interval (seconds)</Label>
                      <Input
                        id="pollIntervalSeconds"
                        type="number"
                        {...register('pollIntervalSeconds', { valueAsNumber: true })}
                      />
                      {errors.pollIntervalSeconds && (
                        <p className="text-sm text-destructive">
                          {errors.pollIntervalSeconds.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">Minimum 5 seconds.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="pollMaxAttempts">Max Poll Attempts</Label>
                      <Input
                        id="pollMaxAttempts"
                        type="number"
                        {...register('pollMaxAttempts', { valueAsNumber: true })}
                      />
                      {errors.pollMaxAttempts && (
                        <p className="text-sm text-destructive">{errors.pollMaxAttempts.message}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Total wait ≈ interval × attempts.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="metadata" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label htmlFor="metadataUpdatesJson">
                  Metadata Update Bindings (optional)
                </Label>
                <textarea
                  id="metadataUpdatesJson"
                  rows={6}
                  className="font-mono text-xs w-full rounded-md border border-input bg-background px-3 py-2"
                  placeholder='[{"metaKey":"plan_type","valueFrom":"$.meta.planType"}]'
                  {...register('metadataUpdatesJson')}
                />
                {errors.metadataUpdatesJson && (
                  <p className="text-sm text-destructive">
                    {errors.metadataUpdatesJson.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  JSON array of{' '}
                  <code className="font-mono">{'{ metaKey, valueFrom }'}</code> bindings, applied
                  for <span className="font-medium">both execution modes</span> whenever bindings
                  are present. Each <code className="font-mono">valueFrom</code> is a JSONPath
                  expression (e.g. <code className="font-mono">$.meta.planType</code>) evaluated
                  against the migrate endpoint's response body (Synchronous) or the job status
                  result (Async Polled); the resolved value may be a string, number, or boolean.
                  After the module is confirmed in Amplify, the orchestrator PATCHes
                  <code className="font-mono"> /modules/{'{moduleId}'}/meta</code> once per
                  binding. Bindings are kept when switching execution modes.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="validation" className="space-y-4 mt-0">
              <div className="space-y-2">
                <Label htmlFor="sourceIdPlaceholder">ID Format (Display)</Label>
                <Input
                  id="sourceIdPlaceholder"
                  placeholder="ORG-XXXXX"
                  {...register('sourceIdPlaceholder')}
                />
                <p className="text-xs text-muted-foreground">
                  Human-readable format shown to users
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sourceIdValidationRegex">ID Validation Regex</Label>
                <Input
                  id="sourceIdValidationRegex"
                  placeholder="^ORG-[A-Z0-9]{5}$"
                  className="font-mono text-sm"
                  {...register('sourceIdValidationRegex')}
                />
              </div>

              {/* Test ID validation */}
              <div className="p-4 border rounded-lg bg-muted/30">
                <Label>Test ID Validation</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder={sourceIdPlaceholder || 'Enter test ID'}
                    className="font-mono flex-1"
                    value={testIdValue}
                    onChange={(e) => {
                      setTestIdValue(e.target.value);
                      setTestIdResult(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (testIdValue.length > 0) {
                          setTestIdResult(testIdFormat(testIdValue));
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    disabled={testIdValue.length === 0}
                    onClick={() => setTestIdResult(testIdFormat(testIdValue))}
                  >
                    <FlaskConical className="mr-2 h-4 w-4" />
                    Test
                  </Button>
                </div>
                {testIdResult !== null && (
                  <div className={`flex items-center gap-2 mt-3 text-sm ${testIdResult ? 'text-green-600' : 'text-destructive'}`}>
                    {testIdResult ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        <span>Valid — matches the expected format.</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4" />
                        <span>Invalid — does not match the expected format{sourceIdPlaceholder ? ` (${sourceIdPlaceholder})` : ''}.</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="migration" className="space-y-4 mt-0">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium">Custom Properties</h4>
                  <p className="text-xs text-muted-foreground">
                    Static configuration values sent to the platform API during migration.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addProperty}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Property
                </Button>
              </div>

              {migrationProperties.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border rounded-lg bg-muted/30">
                  <p className="text-sm">No migration properties configured.</p>
                  <p className="text-xs mt-1">
                    Add properties that will be included in every migration request for this module.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {migrationProperties.map((prop, index) => (
                    <div
                      key={index}
                      className="border rounded-lg p-3 space-y-3"
                    >
                      <div className="flex items-start justify-between">
                        <div className="grid grid-cols-2 gap-3 flex-1">
                          <div className="space-y-1">
                            <Label className="text-xs">Key</Label>
                            <Input
                              value={prop.key}
                              onChange={(e) => updateProperty(index, 'key', e.target.value)}
                              placeholder="pricingPlan"
                              className="font-mono text-sm h-8"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Label</Label>
                            <Input
                              value={prop.label}
                              onChange={(e) => updateProperty(index, 'label', e.target.value)}
                              placeholder="Pricing Plan"
                              className="text-sm h-8"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 ml-2 text-muted-foreground hover:text-destructive"
                          onClick={() => removeProperty(index)}
                          aria-label={`Remove property ${prop.key || index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Type</Label>
                          <Select
                            value={prop.type}
                            onValueChange={(value: MigrationPropertyType) =>
                              updateProperty(index, 'type', value)
                            }
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="string">String</SelectItem>
                              <SelectItem value="integer">Integer</SelectItem>
                              <SelectItem value="boolean">Boolean</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Value</Label>
                          {prop.type === 'boolean' ? (
                            <div className="flex items-center h-8 gap-2">
                              <Switch
                                checked={prop.value === true}
                                onCheckedChange={(checked) =>
                                  updateProperty(index, 'value', checked)
                                }
                              />
                              <span className="text-sm text-muted-foreground">
                                {prop.value === true ? 'true' : 'false'}
                              </span>
                            </div>
                          ) : prop.type === 'integer' ? (
                            <Input
                              type="number"
                              value={typeof prop.value === 'number' ? prop.value : ''}
                              onChange={(e) =>
                                updateProperty(
                                  index,
                                  'value',
                                  e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                                )
                              }
                              className="text-sm h-8"
                            />
                          ) : (
                            <Input
                              value={typeof prop.value === 'string' ? prop.value : String(prop.value)}
                              onChange={(e) => updateProperty(index, 'value', e.target.value)}
                              placeholder="value"
                              className="text-sm h-8"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormDirty || isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
